import React, { useState, useRef, useCallback } from 'react';
import { X, RotateCcw, Paperclip } from 'lucide-react';

interface ReturnStepModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (justification: string, files?: File[]) => void;
  isLoading?: boolean;
}

export const ReturnStepModal: React.FC<ReturnStepModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [justification, setJustification] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  const canConfirm = justification.trim().length >= 10;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(justification.trim(), files.length > 0 ? files : undefined);
  };

  const handleClose = () => {
    setJustification('');
    setFiles([]);
    onClose();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) setFiles(prev => [...prev, ...selected]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-zinc-900 rounded shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-2">
            <RotateCcw size={18} className="text-orange-500" />
            <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-100">
              Retornar Etapa
            </span>
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-zinc-600">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
              Motivo da devolução (mín. 10 caracteres)
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Descreva o motivo para devolver esta etapa..."
              rows={4}
              autoFocus
              className="w-full text-sm border border-zinc-300 dark:border-zinc-600 rounded px-3 py-2 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 resize-none"
            />
          </div>

          {/* Attachment */}
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
              Evidência (opcional)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
            >
              <Paperclip size={12} />
              Anexar arquivo
            </button>
            {files.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-[9px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    <Paperclip size={7} />
                    {f.name}
                    <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="ml-0.5 text-indigo-400 hover:text-indigo-700">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-zinc-200 dark:border-zinc-700">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-3 py-1.5 text-xs rounded border border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
            className="px-3 py-1.5 text-xs rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Devolvendo...' : 'Devolver Etapa'}
          </button>
        </div>
      </div>
    </div>
  );
};
